import React from 'react'
import { LoadingSpinner, SettingsPage } from '@sales-center/components'
import Box from '@mui/material/Box'
import { getStyles } from '@user/components/Settings/PendingLeadSettings/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import { useData } from '@user/components/Settings/PendingLeadSettings/hooks/useData'
import { useHandlers } from '@user/components/Settings/PendingLeadSettings/hooks/useHandlers'
import { AutoReviewConfigurations } from '@user/components/Settings/PendingLeadSettings/AutoReviewConfigurations'
import { ApplicableRules } from '@user/components/Settings/PendingLeadSettings/ApplicableRules'
import { RequiredFieldsSection } from '@user/components/Settings/PendingLeadSettings/RequiredFieldsSection'

export function PendingLeadSettings() {
  const isMobile = useMediaQuery('(max-width:900px)')
  const styles = getStyles(isMobile)

  const {
    autoReviewEnabled,
    selectedTypes,
    visibleSelectedTypes,
    leadTypes,
    loading,
    loadError,
    leadTypesWithoutRule,
    leadTypeAssignmentRules,
    requiredAdditionalFields,
    setRequiredAdditionalFields,
    fieldOptions,
    resyncRequiredFields,
    save,
  } = useData()

  const {
    handleToggleAutoReview,
    handleToggleType,
    handleManageAssignmentRules,
    handleManageFormSettings,
    handleAddFields,
    handleRemoveField,
  } = useHandlers({
    autoReviewEnabled,
    selectedTypes,
    requiredAdditionalFields,
    setRequiredAdditionalFields,
    resyncRequiredFields,
    save,
  })

  return (
    <SettingsPage padding="0">
      <Box sx={styles.centered}>
        {loading ? (
          <Box minHeight="48px" pt={10}>
            <LoadingSpinner />
          </Box>
        ) : (
          <Box sx={styles.main}>
            <Paper variant="outlined" sx={styles.paper}>
              <Typography variant="h5">Pending Lead Settings</Typography>
              <Typography variant="body2">
                Automatically review and convert pending leads into leads once
                required information is captured. When enabled, pending leads
                that satisfy all system-required fields below are automatically
                converted into leads and assigned using your assignment rules.
              </Typography>
            </Paper>

            {loadError ? (
              <Alert
                severity="error"
                data-test-id="pending-lead-settings-load-error"
              >
                Unable to load Pending Lead settings. Refresh the page to try
                again.
              </Alert>
            ) : (
              <>
                <AutoReviewConfigurations
                  leadTypes={leadTypes}
                  leadTypesWithoutRule={leadTypesWithoutRule}
                  autoReviewEnabled={autoReviewEnabled}
                  selectedTypes={selectedTypes}
                  handleToggleAutoReview={handleToggleAutoReview}
                  handleToggleType={handleToggleType}
                  handleManageAssignmentRules={handleManageAssignmentRules}
                />

                {autoReviewEnabled &&
                  leadTypeAssignmentRules.length > 0 &&
                  visibleSelectedTypes.length > 0 &&
                  leadTypesWithoutRule.length === 0 && (
                    <>
                      <ApplicableRules
                        leadTypeAssignmentRules={leadTypeAssignmentRules}
                      />

                      <RequiredFieldsSection
                        leadTypes={leadTypes}
                        visibleSelectedTypes={visibleSelectedTypes}
                        requiredAdditionalFields={requiredAdditionalFields}
                        fieldOptions={fieldOptions}
                        handleAddFields={handleAddFields}
                        handleRemoveField={handleRemoveField}
                        handleManageFormSettings={handleManageFormSettings}
                      />
                    </>
                  )}
              </>
            )}
          </Box>
        )}
      </Box>
    </SettingsPage>
  )
}

PendingLeadSettings.displayName = 'PendingLeadSettings'
